// Wraps fetch with automatic retry on transient network failures.
// "Failed to fetch" / 502 / 503 / 504 happen frequently on showroom WiFi
// and at Cloudflare edge during slow image-generation calls.
//
// Retry policy (Sept 2026):
//   GET / HEAD           retried up to `retries` times.
//   POST / PUT / PATCH   retried ONLY when the request carries an
//                        Idempotency-Key header, because the backend then
//                        returns the same job instead of billing a second
//                        render. Without the key a POST is sent once.
//
// When a retry succeeds, we dispatch a global "hedaply:retry-success"
// CustomEvent so the UI can show a small reassurance toast.

export function newIdempotencyKey() {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  } catch {
    // fall through
  }
  return `k-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

function hasIdempotencyKey(headers) {
  if (!headers) return false;
  if (typeof Headers !== "undefined" && headers instanceof Headers) {
    return headers.has("Idempotency-Key");
  }
  return Object.keys(headers).some((k) => k.toLowerCase() === "idempotency-key");
}

export async function resilientFetch(
  url,
  options = {},
  { retries = 2, backoffMs = 800 } = {}
) {
  const method = (options.method || "GET").toUpperCase();
  const safe = method === "GET" || method === "HEAD";
  const allowedRetries = safe || hasIdempotencyKey(options.headers) ? retries : 0;

  let lastErr;
  for (let attempt = 0; attempt <= allowedRetries; attempt += 1) {
    try {
      const res = await fetch(url, options);
      // A 5xx that carries our backend's marker header is a real answer
      // (the paid work already ran); only edge / gateway 5xx are retried.
      const fromApp = !!(res.headers && res.headers.get && res.headers.get("x-app-response"));
      if ([502, 503, 504].includes(res.status) && !fromApp && attempt < allowedRetries) {
        await new Promise((r) => setTimeout(r, backoffMs * (attempt + 1)));
        continue;
      }
      if (attempt > 0 && res.ok && typeof window !== "undefined") {
        try {
          window.dispatchEvent(new CustomEvent("hedaply:retry-success"));
        } catch {
          // no-op on browsers without CustomEvent
        }
      }
      return res;
    } catch (err) {
      lastErr = err;
      if (attempt < allowedRetries) {
        await new Promise((r) => setTimeout(r, backoffMs * (attempt + 1)));
        continue;
      }
      throw err;
    }
  }
  throw lastErr || new Error("Network error");
}
